import useUrlState from '@ahooksjs/use-url-state';
import { ProFormInstance } from '@ant-design/pro-form';
import { useLocalStorageState } from 'ahooks';
import { isEmpty } from 'lodash-es';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as uuid from 'uuid';
import { useAppConfig } from '@/config/context';
import { isAppleTV, isIOS, isVisionOS } from '@/infrastructure/product-info';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import { isDataLabelingPlatform } from '@/infrastructure/utils/issue';
import { DenominatorTag } from '@/presentation/components/denominator-tag';
import {
  CHARACTER_MATCH_ARRAY_OPTIONS,
  CHARACTER_MATCH_OPTIONS,
  INFERRED_LABELS_OPTIONS,
  MatchMode,
  NUMBER_MATCH_OPTIONS, VERSION_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import { FilterEntry } from '@/presentation/components/form/types';
import { ABTEST_TIPS, BUNDLE_TIPS_CASE, hintText, iconText } from '@/presentation/components/hint-text/hintText';
import {
  DATA_LABELING_TIPS,
  numberFilterTips,
  versionFilterTips,
} from '@/presentation/components/issue-list/components/tips';
import { CompareParams, TdsFilterType, TdsForm, TdsFormItem, TdsFormItemType } from '@/presentation/components/tds-form';
import { DEFAULT_EVENT_TIME_RANGE } from '@/presentation/components/tds-form/config';
import { TdsCustomFieldsValue } from '@/presentation/components/tds-form/tds-custom-fields';
import { getInitTimeRange } from '@/presentation/components/tds-form/tds-date-cascader';
import {
  COMMON_TIME_TYPE_OPTIONS,
  ISSUE_TIME_TYPE,
} from '@/presentation/components/tds-form/tds-date-cascader/config';
import { TdsColumnGroup } from '@/presentation/components/tds-form/type';
import {
  customFields2Filter,
  dateCascaderConverter,
  dateCascaderTransformer, mbFilter,
} from '@/presentation/components/tds-form/utils';
import { useTdsForm } from '@/presentation/hooks/form';
import { HEAD_FORM_NAME } from '@/presentation/pages/foom/case/list/config';
import { getIssueFilterConfig } from '@/presentation/pages/setting/data-labeling/config';
import { useStores } from '@/presentation/store';
import {
  CommonFieldKey,
  FieldKey,
  IOS_FOOM_LINKAGES_OPTION,
  StandardCommonFieldKey,
  Zone,
} from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { IFilter } from '@/proto/metric';
import { genStandardCompareToken, genStandardToken, KeyDataType } from '@/proto/token';

interface IProps {
  entry: FilterEntry;
  onTokenChange?: (token: string) => void;
}


export const ProblemForm = (props: IProps) => {
  const { t } = useTranslation();
  const { entry, onTokenChange } = props;
  const config = useAppConfig();
  const formRef = useRef<ProFormInstance>();
  const compareFormRef = useRef<ProFormInstance>();
  const [, useTdsFormContext] = useTdsForm;
  const { formContext, updateFormContext } = useTdsFormContext();
  const enableCompare = entry === FilterEntry.MemoryHighList;
  const [showSwitch, setShowSwitch] = useState(false);
  // 无堆栈问题聚类方式，需要在详情里传参
  const [standardIsInferenceFeature, setStandardIsInferenceFeature] = useState();
  const [, setState] = useUrlState({
    token: '',
  });
  const [timeRange, setTimeRange] = useLocalStorageState(
    'memory-high-list-v1',
    {
      defaultValue: DEFAULT_EVENT_TIME_RANGE,
    },
  );

  const { filterConfig } = useStores().ProductStore;
  const inferredLabelsConfig = getIssueFilterConfig(IssueType.ISSUE_TYPE_HIGH_MEMORY, filterConfig);

  const labelTools = enableCompare ? [<DenominatorTag title={t('支持联网下钻')} />] : undefined;

  const formItems: TdsFormItem[] = [
    {
      type: TdsFormItemType.DateCascader,
      label: t('时间范围'),
      name: StandardCommonFieldKey.TimeRange,
      width: 462,
      deleteable: false,
      defaultEditHidden: false,
      labelTools,
      convertValue: dateCascaderConverter,
      transform: dateCascaderTransformer,
      fieldProps: {
        maxLimitedDays: 90,
        timeWindowOptions: ISSUE_TIME_TYPE,
        timeTypeOptions: COMMON_TIME_TYPE_OPTIONS,
      },
      inFilter: true,
      filterType: TdsFilterType.DateCascader,
    },
    {
      type: TdsFormItemType.CharacterInput,
      width: 360,
      batchEditMode: true,
      defaultEditHidden: false,
      batchEditTips: versionFilterTips,
      label: t('APP版本'),
      labelString: t('APP版本'),
      name: StandardCommonFieldKey.AppVersion,
      labelTools,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.AppVersion,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.AppVersion,
      filterType: TdsFilterType.Version,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('系统版本'),
      name: StandardCommonFieldKey.OsVersion,
      group: TdsColumnGroup.DeviceInfo,
      batchEditMode: true,
      defaultEditHidden: true,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.OsVersion,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.OsVersion,
      filterType: TdsFilterType.Version,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('SDK版本'),
      name: StandardCommonFieldKey.SdkVersion,
      group: TdsColumnGroup.ApplicationInfo,
      labelTools,
      batchEditMode: true,
      defaultEditHidden: true,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.SdkVersion,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.SdkVersion,
      filterType: TdsFilterType.Version,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('机型'),
      name: StandardCommonFieldKey.Model,
      batchEditMode: true,
      defaultEditHidden: true,
      group: TdsColumnGroup.DeviceInfo,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Model,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Model,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('厂商'),
      group: TdsColumnGroup.DeviceInfo,
      name: StandardCommonFieldKey.Brand,
      batchEditMode: true,
      defaultEditHidden: true,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Brand,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.Brand,
      filterType: TdsFilterType.String,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('场景'),
      name: StandardCommonFieldKey.Stage,
      group: TdsColumnGroup.ContextInfo,
      inFilter: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Stage,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Stage,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('用户ID'),
      batchEditMode: true,
      defaultEditHidden: true,
      group: TdsColumnGroup.UserInfo,
      name: StandardCommonFieldKey.AccountId,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.AccountId,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.AccountId,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('设备ID'),
      batchEditMode: true,
      defaultEditHidden: true,
      name: StandardCommonFieldKey.Qimei,
      group: TdsColumnGroup.UserInfo,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Qimei,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Qimei,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('启动ID'),
      batchEditMode: true,
      defaultEditHidden: true,
      group: TdsColumnGroup.ContextInfo,
      labelString: t('启动ID'),
      name: StandardCommonFieldKey.LaunchId,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.LaunchId,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.LaunchId,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('消息ID'),
      batchEditMode: true,
      defaultEditHidden: true,
      group: TdsColumnGroup.exceptionInfo,
      name: StandardCommonFieldKey.ClientIdentify,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.ClientIdentify,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.ClientIdentify,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'Issue ID',
      batchEditMode: true,
      defaultEditHidden: true,
      group: TdsColumnGroup.exceptionInfo,
      name: StandardCommonFieldKey.Feature,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Feature,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Feature,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      hidden: entry !== FilterEntry.MemoryHighList,
    },


    {
      type: TdsFormItemType.NumberInput,
      label: t('FOOM前内存（MB）'),
      labelString: t('FOOM前内存（MB）'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardCommonFieldKey.UsedMem,
      fieldProps: {
        modeOptions: [{
          value: MatchMode.Between,
          label: t('区间'),
        }],
      },
      filterName: CommonFieldKey.UsedMem,
      filterType: TdsFilterType.Custom,
      inFilter: true,
      defaultEditHidden: true,
      filterTransform: (fieldKey, fieldValue) => mbFilter(
        fieldKey,
        MatchMode.Between,
        fieldValue?.values?.min,
        fieldValue?.values?.max,
      ),
      hidden: ![FilterEntry.MemoryHighList, FilterEntry.MemoryHighDetail].includes(entry),
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('问题特征'),
      labelString: t('问题特征'),
      group: TdsColumnGroup.exceptionInfo,
      name: StandardCommonFieldKey.FuncName,
      inFilter: true,
      defaultEditHidden: true,
      hidden: entry !== FilterEntry.MemoryHighList,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.FuncName,
      batchEditMode: true,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      group: TdsColumnGroup.ApplicationInfo,
      labelTools: [iconText(BUNDLE_TIPS_CASE), ...(labelTools || [])],
      label: 'Bundle ID',
      labelString: 'Bundle ID',
      name: StandardCommonFieldKey.BundleId,
      inFilter: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.BundleId,
      batchEditMode: true,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.BundleId,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('实验ID'),
      labelTools: [iconText(ABTEST_TIPS), ...(labelTools || [])],
      group: TdsColumnGroup.Custom,
      name: StandardCommonFieldKey.ShiplyAbTestTag,
      filterName: CommonFieldKey.ShiplyAbTestTag,
      inFilter: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(
        CommonFieldKey.ShiplyAbTestTag,
        fieldValue,
        'a11',
      ),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.ShiplyTag,
          fieldScope: Zone.HighMemory,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
      hidden: !config.enableShiplyAbTestTag,
    },
    {
      type: TdsFormItemType.CharacterInput,
      hidden: !config.enableAbTestTag,
      label: t('业务下钻'),
      labelTools: [iconText(ABTEST_TIPS), ...(labelTools || [])],
      group: TdsColumnGroup.Custom,
      name: StandardCommonFieldKey.AbTestTag,
      filterName: CommonFieldKey.ShiplyAbTestTag,
      inFilter: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(
        CommonFieldKey.ShiplyAbTestTag,
        fieldValue,
        'a12',
      ),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.AbTestTag,
          fieldScope: Zone.HighMemory,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.FieldSelector,
      name: StandardCommonFieldKey.Linkages,
      filterName: CommonFieldKey.Linkages,
      inFilter: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.StringArray,
      labelString: t('联动标记'),
      group: TdsColumnGroup.Tag,
      label: t('联动标记'),
      fieldProps: {
        mode: 'tags',
        options: IOS_FOOM_LINKAGES_OPTION,
      },
      hidden:
        ![FilterEntry.MemoryHighList, FilterEntry.MemoryHighDetail].includes(props.entry)
        || (!isIOS() && !isVisionOS() && !isAppleTV()),
      hideInCompare: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('个例标签'),
      group: TdsColumnGroup.Tag,
      name: StandardCommonFieldKey.TagsInFilter,
      filterName: CommonFieldKey.TagsInFilter,
      inFilter: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(
        CommonFieldKey.TagsInFilter,
        fieldValue,
        'a13',
      ),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.TagsInFilter,
          fieldScope: Zone.HighMemory,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
      hideInCompare: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      group: TdsColumnGroup.Tag,
      labelTools: [iconText(DATA_LABELING_TIPS)],
      label: t('数据标记'),
      labelString: t('数据标记'),
      name: StandardCommonFieldKey.StandardInferredLabels,
      filterName: CommonFieldKey.StandardInferredLabels,
      inFilter: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.Custom,
      initialValue: {
        mode: MatchMode.NOT,
        values: inferredLabelsConfig?.values?.map(item => item.value),
      },
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(
        CommonFieldKey.StandardInferredLabels,
        fieldValue,
      ),
      fieldProps: {
        valueSelectProps: {
          options: inferredLabelsConfig?.labelOptions || [],
        },
        mode: 'tags',
        modeOptions: INFERRED_LABELS_OPTIONS,
      },
      hideInCompare: true,
      hidden: !isDataLabelingPlatform(),
    },
    {
      type: TdsFormItemType.NumberInput,
      labelTools: [iconText(numberFilterTips)],
      label: t('使用时长（秒）'),
      labelString: t('使用时长（秒）'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardCommonFieldKey.StandardLaunchDuration,
      fieldProps: {
        modeOptions: NUMBER_MATCH_OPTIONS,
      },
      inFilter: true,
      defaultEditHidden: true,
      filterName: CommonFieldKey.LaunchDuration,
      filterType: TdsFilterType.Number,
    },
    {
      type: TdsFormItemType.Area,
      inFilter: true,
      name: StandardCommonFieldKey.Area,
      label: t('地域'),
      group: TdsColumnGroup.UserInfo,
      fieldProps: {
        fieldScope: Zone.HighMemory,
      },
      hideInCompare: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.Custom,
      filterName: StandardCommonFieldKey.Area,
      filterTransform: (_fieldKey: string, fieldValue: TdsCustomFieldsValue[]) => fieldValue?.map((item: any) => ({
        name: item.name,
        operator: item.operator,
        values: item.values || [],
      })) ?? [],
    },
    {
      type: TdsFormItemType.CustomPanel,
      label: t('自定义维度'),
      name: StandardCommonFieldKey.UserCustom,
      group: TdsColumnGroup.Custom,
      inFilter: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.Custom,
      filterName: CommonFieldKey.UserCustom,
      fieldProps: {
        issueType: IssueType.ISSUE_TYPE_HIGH_MEMORY,
        type: 'customFields',
      },
      filterTransform: (_fieldKey: string, fieldValue: TdsCustomFieldsValue[]) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.UserCustom,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: KeyDataType.STRING,
      })) ?? [],
    },
    {
      type: TdsFormItemType.CustomPanel,
      label: t('自定义字段'),
      group: TdsColumnGroup.Custom,
      name: StandardCommonFieldKey.BizExtendInfo,
      inFilter: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.Custom,
      filterName: CommonFieldKey.BizExtendInfo,
      fieldProps: {
        issueType: IssueType.ISSUE_TYPE_HIGH_MEMORY,
        type: 'customKey',
      },
      filterTransform: (_fieldKey: string, fieldValue: TdsCustomFieldsValue[]) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.BizExtendInfo,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: KeyDataType.STRING,
      })) ?? [],
    },
    {
      type: TdsFormItemType.FieldSelector,
      name: 'showNew',
      label: t('只显示新增问题'),
      hidden: !showSwitch || props.entry !== FilterEntry.MemoryHighList,
      deleteable: false,
      defaultEditHidden: false,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: FieldKey.ShowNew,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      hideInCompare: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: hintText(t('构建号'), ABTEST_TIPS),
      labelTools: [iconText(ABTEST_TIPS)],
      labelString: t('构建号'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardCommonFieldKey.BuildNumber,
      inFilter: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.BuildNumber,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.BuildNumber,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.FieldSelector,
      name: StandardCommonFieldKey.OriginalStacks,
      group: TdsColumnGroup.ContextInfo,
      label: t('有无内存分配堆栈'),
      inFilter: true,
      defaultEditHidden: true,
      filterType: TdsFilterType.StringArray,
      filterName: CommonFieldKey.OriginalStacks,
      fieldProps: {
        mode: 'single',
        valueSelectProps: {
          fieldKey: CommonFieldKey.OriginalStacks,
          fieldScope: Zone.HighMemory,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      hidden: ![FilterEntry.MemoryHighDetail].includes(entry),
    },
  ];

  const onFieldsChange = (fields: any) => {
    if (fields?.[0]?.name?.includes(StandardCommonFieldKey.AppVersion)) {
      setShowSwitch(fields?.[0]?.value?.mode === MatchMode.OR && !isEmpty(fields?.[0]?.value?.values));
    }
  };

  const onCompareFinish = async (values: CompareParams) => {
    const token = await genStandardCompareToken(values);

    updateFormContext?.(draft => ({
      ...draft,
      valueMap: {
        ...(draft?.valueMap || {}),
        [HEAD_FORM_NAME]: {
          ...(draft?.valueMap?.[HEAD_FORM_NAME] || {}),
          token,
          fieldsValue: values.fieldsValue,
          fieldsFilter: values.fieldsFilters,
          compareFieldsValue: values.compareFieldsValue,
          compareFieldsFilter: values.compareFieldsFilters,
          uuidFetch: uuid.v4(),
        },
      },
    }));
    if (!enableCompare) {
      setState({
        token,
      });
    }
  };

  const onFinish = async (values: Record<string, any>, filters: IFilter[]) => {
    // 判断是详情页，保存 standard_is_inference_feature
    if (!standardIsInferenceFeature && values?.standard_is_inference_feature && !enableCompare) {
      setStandardIsInferenceFeature(values?.standard_is_inference_feature);
    }
    // 刷新或使用链接打开时过滤掉token里表格的表单filters，因为趋势分析使用的token带上表格的参数可能报错
    let newFilters = filters?.filter(item => !([CommonFieldKey.FeatureShow,
      CommonFieldKey.IsInferenceFeature,
      CommonFieldKey.AvgMaxMallocSize,
      CommonFieldKey.MaxMallocSize,
      CommonFieldKey.QimeiCount,
      CommonFieldKey.DocCount,
      CommonFieldKey.P90MaxMallocSize,
      CommonFieldKey.P50MaxMallocSize].some(str => str === item?.name)));
    const isInferenceFeature = standardIsInferenceFeature || values?.standard_is_inference_feature;
    if (!enableCompare && isInferenceFeature && !filters?.some(item => item.name === CommonFieldKey.FeatureShow
      || item.name === CommonFieldKey.IsInferenceFeature)) {
      newFilters = [...filters,
        {
          name: isInferenceFeature === MatchMode.NOT
            ? CommonFieldKey.FeatureShow : CommonFieldKey.IsInferenceFeature,
          values: [isInferenceFeature === MatchMode.NOT ? t('无堆栈问题') : '0'],
          operator: isInferenceFeature || '',
        }];
    }
    const formatValues = {
      ...values,
      timeWindow: values?.time_window_type,
    };
    // 缓存选择过的时间范围
    setTimeRange(values?.[StandardCommonFieldKey.TimeRange] ?? DEFAULT_EVENT_TIME_RANGE);
    const token = await genStandardToken({
      ...formatValues,
      filters: newFilters,
    });

    updateFormContext?.(draft => ({
      ...draft,
      valueMap: {
        ...(draft?.valueMap || {}),
        [HEAD_FORM_NAME]: {
          ...(draft?.valueMap?.[HEAD_FORM_NAME] || {}),
          token,
          fieldsValue: values,
          fieldsFilter: newFilters,
          uuidFetch: uuid.v4(),
          compareFieldsValue: undefined,
          compareFieldsFilter: undefined,
        },
      },
    }));

    if (!enableCompare) {
      setState({
        token,
      });
    }
    onTokenChange?.(token);
  };


  const initialTimeRange = useMemo(() => (timeRange ? {
    ...timeRange,
    date_value: getInitTimeRange(timeRange.time_window_type!, timeRange.short_cut!),
  } : timeRange), [timeRange]);

  return (
    <TdsForm
      onFieldsChange={onFieldsChange}
      onCompareFinish={onCompareFinish}
      enableCompare={enableCompare}
      items={formItems}
      compareFormRef={compareFormRef}
      formRef={formRef}
      name={'memory-high-issues'}
      pageId={PAGE_ID.memory.high.list}
      onFinish={onFinish}
      compareOpen={enableCompare ? formContext.isCompare : false}
      tokenControl={{
        refreshToken: true,
        isExpiredDateModal: {
          isAnalytics: entry === FilterEntry.MemoryHighAnalysis,
          initDate: DEFAULT_EVENT_TIME_RANGE,
        },
      }}
      initialValues={{
        [StandardCommonFieldKey.TimeRange]: initialTimeRange,
      }}
      renderByGroup
    />
  );
};
